import { Grid, Typography } from "@mui/material";
import { memo, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CandidateDetails } from "../../../redux/Hire/jobRequestApi";
import { useGetTaskForCandidateAtStageQuery } from "../../../redux/Task/TaskApi";
import StatusManager from "../CandidateStatus/StatusManager";
import CandidateResume from "../Compoenents/CandidateResume";
import DisqualifyCandidate from "../Compoenents/DisqualifyCandidate";
import MoveCandidate from "../Compoenents/MoveCandidate";
import RestoreCandidate from "../Compoenents/RestoreCandidate";

type Props = {
  candidate?: CandidateDetails;
  jobId?: string;
};

export const CODE_MAP: Record<string, string> = {
  shortlist: "internalInterview",
  offerAccepted: "startOnboarding",
};

// Custom hook to encapsulate polling with refetch and stop conditions
export function usePollingWithRefetch({
  enabled,
  result,
  refetch,
  deps = [],
  maxRetries = 5,
  interval = 3000,
}: {
  enabled: boolean;
  result: any;
  refetch: () => Promise<any>;
  deps?: any[];
  maxRetries?: number;
  interval?: number;
}) {
  const [retryCount, setRetryCount] = useState(0);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (result != null) {
      if (pollingRef.current) clearTimeout(pollingRef.current);
      return;
    }
    if (retryCount >= maxRetries) {
      if (pollingRef.current) clearTimeout(pollingRef.current);
      return;
    }
    pollingRef.current = setTimeout(async () => {
      await refetch();
      setRetryCount((prev) => prev + 1);
    }, interval);
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, retryCount, result, ...deps]);

  useEffect(() => {
    if (!enabled) setRetryCount(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);
}

const DetailsHeader = ({ candidate, jobId }: Props) => {
  const enabled = !!candidate?.id && !!candidate?.jobId && !!candidate?.stage;

  const { data: taskData, refetch } = useGetTaskForCandidateAtStageQuery(
    {
      CandidateId: candidate?.id?.toString() || "",
      JobId: candidate?.jobId || "",
      StageCode: CODE_MAP[candidate?.stage || ""]
        ? CODE_MAP[candidate?.stage || ""]
        : candidate?.stage || "",
    },
    {
      skip: !enabled,
    },
  );

  usePollingWithRefetch({
    enabled,
    result: taskData?.data?.result,
    refetch,
    deps: [candidate?.id, candidate?.jobId, candidate?.stage],
    maxRetries: 5,
    interval: 3000,
  });

  return (
    <Grid container bgcolor={"#fff"} p={4} rowGap={2}>
      <Grid item xs={12}>
        <Link to={`/candidates/${candidate?.id}?jobId=${jobId}`}>
          <Typography fontSize={"24px"} fontWeight={600} lineHeight={"24px"}>
            {candidate?.name || "Candidate Name"}
          </Typography>
        </Link>
      </Grid>
      <Grid container item xs={12} alignItems={"center"} gap={2} wrap="nowrap">
        <Grid item xs={6}>
          <StatusManager candidate={candidate} jobId={jobId || ""} />
        </Grid>
        <Grid container item justifyContent={"flex-end"} xs={6} gap={2}>
          {!taskData?.data?.result ? (
            candidate?.disqualified ? (
              <RestoreCandidate candidate={candidate} />
            ) : (
              <Typography>No Task Found</Typography>
            )
          ) : null}

          {taskData?.data?.result && !candidate?.disqualified ? (
            <>
              <DisqualifyCandidate
                candidate={candidate}
                task={taskData.data.result}
              />
              <MoveCandidate
                candidate={candidate}
                task={taskData.data.result}
              />
            </>
          ) : null}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <CandidateResume candidate={candidate} />
      </Grid>
    </Grid>
  );
};

export default memo(DetailsHeader);
